/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as vscode from 'vscode';
import { Command, $Chc } from '../commands/commandManager';
import { DocumentSelector } from '../configuration/documentSelector';
import { TelemetryReporter } from '../logging/telemetry';
import * as fixNames from '../tsServer/protocol/fixNames';
import type * as Proto from '../tsServer/protocol/protocol';
import * as typeConverters from '../typeConverters';
import { ClientCapability, ITypeScriptServiceClient } from '../typescriptService';
import { $zic } from '../utils/cancellation';
import { $gic } from '../utils/memoize';
import { $Dhc } from '../utils/objects';
import { $aic } from './diagnostics';
import $Kic from './fileConfigurationManager';
import { $8ic, $6ic } from './util/codeAction';
import { $Sic, $Vic } from './util/dependentRegistration';

type ApplyCodeActionCommand_args = {
	readonly document: vscode.TextDocument;
	readonly diagnostic: vscode.Diagnostic;
	readonly action: Proto.CodeFixAction;
	readonly followupAction?: Command;
};

class EditorChatFollowUp implements Command {

	id: string = '_typescript.quickFix.editorChatFollowUp';

	constructor(private readonly a: string, private readonly b: vscode.TextDocument, private readonly c: vscode.Range, private readonly d: ITypeScriptServiceClient) {
	}

	async execute() {
		const findScopeEndLineFromNavTree = (startLine: number, navigationTree: Proto.NavigationTree[]): vscode.Range | undefined => {
			for (const node of navigationTree) {
				const range = typeConverters.Range.fromTextSpan(node.spans[0]);
				if (startLine === range.start.line) {
					return range;
				} else if (startLine > range.start.line && startLine <= range.end.line && node.childItems) {
					return findScopeEndLineFromNavTree(startLine, node.childItems);
				}
			}
			return undefined;
		};
		const filepath = this.d.toOpenTsFilePath(this.b);
		if (!filepath) {
			return;
		}
		const response = await this.d.execute('navtree', { file: filepath }, $zic);
		if (response.type !== 'response' || !response.body?.childItems) {
			return;
		}
		const startLine = this.c.start.line;
		const enclosingRange = findScopeEndLineFromNavTree(startLine, response.body.childItems);
		if (!enclosingRange) {
			return;
		}
		await vscode.commands.executeCommand('vscode.editorChat.start', { initialRange: enclosingRange, message: this.a, autoSend: true });
	}
}

class ApplyCodeActionCommand implements Command {
	public static readonly ID = '_typescript.applyCodeActionCommand';
	public readonly id = ApplyCodeActionCommand.ID;

	constructor(
		private readonly a: ITypeScriptServiceClient,
		private readonly b: $aic,
		private readonly c: TelemetryReporter,
	) { }

	public async execute({ document, action, diagnostic, followupAction }: ApplyCodeActionCommand_args): Promise<boolean> {
		/* __GDPR__
			"quickFix.execute" : {
				"owner": "mjbvz",
				"fixName" : { "classification": "PublicNonPersonalData", "purpose": "FeatureInsight" },
				"${include}": [
					"${TypeScriptCommonProperties}"
				]
			}
		*/
		this.c.logTelemetry('quickFix.execute', {
			fixName: action.fixName
		});

		this.b.deleteDiagnostic(document.uri, diagnostic);
		const codeActionResult = await $8ic(this.a, action.commands, $zic);
		await followupAction?.execute();
		return codeActionResult;
	}
}

type ApplyFixAllCodeAction_args = {
	readonly action: VsCodeFixAllCodeAction;
};

class ApplyFixAllCodeAction implements Command {
	public static readonly ID = '_typescript.applyFixAllCodeAction';
	public readonly id = ApplyFixAllCodeAction.ID;

	constructor(
		private readonly a: ITypeScriptServiceClient,
		private readonly b: TelemetryReporter,
	) { }

	public async execute(args: ApplyFixAllCodeAction_args): Promise<void> {
		/* __GDPR__
			"quickFixAll.execute" : {
				"owner": "mjbvz",
				"fixName" : { "classification": "PublicNonPersonalData", "purpose": "FeatureInsight" },
				"${include}": [
					"${TypeScriptCommonProperties}"
				]
			}
		*/
		this.b.logTelemetry('quickFixAll.execute', {
			fixName: args.action.tsAction.fixName
		});

		if (args.action.combinedResponse) {
			await $8ic(this.a, args.action.combinedResponse.body.commands, $zic);
		}
	}
}

/**
 * Unique set of diagnostics keyed on diagnostic range and error code.
 */
class DiagnosticsSet {
	public static from(diagnostics: vscode.Diagnostic[]) {
		const values = new Map<string, vscode.Diagnostic>();
		for (const diagnostic of diagnostics) {
			values.set(DiagnosticsSet.a(diagnostic), diagnostic);
		}
		return new DiagnosticsSet(values);
	}

	private static a(diagnostic: vscode.Diagnostic) {
		const { start, end } = diagnostic.range;
		return `${diagnostic.code}-${start.line},${start.character}-${end.line},${end.character}`;
	}

	private constructor(
		private readonly b: Map<string, vscode.Diagnostic>
	) { }

	public get values(): Iterable<vscode.Diagnostic> {
		return this.b.values();
	}

	public get size() {
		return this.b.size;
	}
}

class VsCodeCodeAction extends vscode.CodeAction {
	constructor(
		public readonly tsAction: Proto.CodeFixAction,
		title: string,
		kind: vscode.CodeActionKind
	) {
		super(title, kind);
	}
}

class VsCodeFixAllCodeAction extends VsCodeCodeAction {
	constructor(
		tsAction: Proto.CodeFixAction,
		public readonly file: string,
		title: string,
		kind: vscode.CodeActionKind
	) {
		super(tsAction, title, kind);
	}

	public combinedResponse?: Proto.GetCombinedCodeFixResponse;
}

class CodeActionSet {
	private readonly a = new Set<VsCodeCodeAction>();
	private readonly b = new Map<{}, VsCodeCodeAction>();

	public get values(): Iterable<VsCodeCodeAction> {
		return this.a;
	}

	public addAction(action: VsCodeCodeAction) {
		for (const existing of this.a) {
			if (action.tsAction.fixName === existing.tsAction.fixName && $Dhc(action.edit, existing.edit)) {
				this.a.delete(existing);
			}
		}

		this.a.add(action);

		if (action.tsAction.fixId) {
			// If we have an existing fix all action, then make sure it follows this action
			const existingFixAll = this.b.get(action.tsAction.fixId);
			if (existingFixAll) {
				this.a.delete(existingFixAll);
				this.a.add(existingFixAll);
			}
		}
	}

	public addFixAllAction(fixId: {}, action: VsCodeCodeAction) {
		const existing = this.b.get(fixId);
		if (existing) {
			// reinsert action at back of actions list
			this.a.delete(existing);
		}
		this.addAction(action);
		this.b.set(fixId, action);
	}

	public hasFixAllAction(fixId: {}) {
		return this.b.has(fixId);
	}
}

class SupportedCodeActionProvider {
	public constructor(
		private readonly a: ITypeScriptServiceClient
	) { }

	public async getFixableDiagnosticsForContext(context: vscode.CodeActionContext): Promise<DiagnosticsSet> {
		const fixableCodes = await this.b;
		return DiagnosticsSet.from(
			context.diagnostics.filter(diagnostic => typeof diagnostic.code !== 'undefined' && fixableCodes.has(diagnostic.code + '')));
	}

	@$gic
	private get b(): Thenable<Set<string>> {
		return this.a.execute('getSupportedCodeFixes', null, $zic)
			.then(response => response.type === 'response' ? response.body || [] : [])
			.then(codes => new Set(codes));
	}
}

class TypeScriptQuickFixProvider implements vscode.CodeActionProvider<VsCodeCodeAction> {

	public static readonly metadata: vscode.CodeActionProviderMetadata = {
		providedCodeActionKinds: [vscode.CodeActionKind.QuickFix]
	};

	private readonly a: SupportedCodeActionProvider;

	constructor(
		private readonly b: ITypeScriptServiceClient,
		private readonly c: $Kic,
		commandManager: $Chc,
		private readonly d: $aic,
		telemetryReporter: TelemetryReporter
	) {
		commandManager.register(new ApplyCodeActionCommand(b, d, telemetryReporter));
		commandManager.register(new ApplyFixAllCodeAction(b, telemetryReporter));

		this.a = new SupportedCodeActionProvider(b);
	}

	public async provideCodeActions(
		document: vscode.TextDocument,
		_range: vscode.Range,
		context: vscode.CodeActionContext,
		token: vscode.CancellationToken
	): Promise<VsCodeCodeAction[] | undefined> {
		const file = this.b.toOpenTsFilePath(document);
		if (!file) {
			return;
		}

		const fixableDiagnostics = await this.a.getFixableDiagnosticsForContext(context);
		if (!fixableDiagnostics.size || token.isCancellationRequested) {
			return;
		}

		if (this.b.bufferSyncSupport.hasPendingDiagnostics(document.uri)) {
			return;
		}

		await this.c.ensureConfigurationForDocument(document, token);
		if (token.isCancellationRequested) {
			return;
		}

		const results = new CodeActionSet();
		for (const diagnostic of fixableDiagnostics.values) {
			await this.e(document, file, diagnostic, results, token);
			if (token.isCancellationRequested) {
				return;
			}
		}

		const allActions = Array.from(results.values);
		for (const action of allActions) {
			action.isPreferred = isPreferredFix(action, allActions);
		}
		return allActions;
	}

	public async resolveCodeAction(codeAction: VsCodeCodeAction, token: vscode.CancellationToken): Promise<VsCodeCodeAction> {
		if (!(codeAction instanceof VsCodeFixAllCodeAction) || !codeAction.tsAction.fixId) {
			return codeAction;
		}

		const arg: Proto.GetCombinedCodeFixRequestArgs = {
			scope: {
				type: 'file',
				args: { file: codeAction.file }
			},
			fixId: codeAction.tsAction.fixId,
		};

		const response = await this.b.execute('getCombinedCodeFix', arg, token);
		if (response.type === 'response') {
			codeAction.combinedResponse = response;
			codeAction.edit = typeConverters.WorkspaceEdit.fromFileCodeEdits(this.b, response.body.changes);
		}

		return codeAction;
	}

	private async e(
		document: vscode.TextDocument,
		file: string,
		diagnostic: vscode.Diagnostic,
		results: CodeActionSet,
		token: vscode.CancellationToken,
	): Promise<CodeActionSet> {
		const args: Proto.CodeFixRequestArgs = {
			...typeConverters.Range.toFileRangeRequestArgs(file, diagnostic.range),
			errorCodes: [+(diagnostic.code!)]
		};
		const response = await this.b.execute('getCodeFixes', args, token);
		if (response.type !== 'response' || !response.body) {
			return results;
		}

		for (const tsCodeFix of response.body) {
			this.f(results, document, file, diagnostic, tsCodeFix as Proto.CodeFixAction);
		}
		return results;
	}

	private f(
		results: CodeActionSet,
		document: vscode.TextDocument,
		file: string,
		diagnostic: vscode.Diagnostic,
		tsAction: Proto.CodeFixAction
	): CodeActionSet {
		results.addAction(this.g(document, diagnostic, tsAction));
		this.h(results, document.uri, file, diagnostic, tsAction as Proto.CodeFixAction);
		return results;
	}

	private g(
		document: vscode.TextDocument,
		diagnostic: vscode.Diagnostic,
		tsAction: Proto.CodeFixAction
	): VsCodeCodeAction {
		const aiQuickFixEnabled = vscode.workspace.getConfiguration('typescript').get('experimental.aiQuickFix');
		let followupAction: Command | undefined;
		if (aiQuickFixEnabled && tsAction.fixName === fixNames.$Bjc) {
			followupAction = new EditorChatFollowUp('Implement the class using the interface', document, diagnostic.range, this.b);
		}
		const codeAction = new VsCodeCodeAction(tsAction, tsAction.description, vscode.CodeActionKind.QuickFix);
		codeAction.edit = $6ic(this.b, tsAction);
		codeAction.diagnostics = [diagnostic];
		codeAction.command = {
			command: ApplyCodeActionCommand.ID,
			arguments: [<ApplyCodeActionCommand_args>{ action: tsAction, diagnostic, document, followupAction }],
			title: ''
		};
		return codeAction;
	}

	private h(
		results: CodeActionSet,
		resource: vscode.Uri,
		file: string,
		diagnostic: vscode.Diagnostic,
		tsAction: Proto.CodeFixAction,
	): CodeActionSet {
		if (!tsAction.fixId || results.hasFixAllAction(tsAction.fixId)) {
			return results;
		}

		// Make sure there are multiple diagnostics of the same type in the file
		if (!this.d.getDiagnostics(resource).some(x => {
			if (x === diagnostic) {
				return false;
			}
			return x.code === diagnostic.code
				|| (fixAllErrorCodes.has(x.code as number) && fixAllErrorCodes.get(x.code as number) === fixAllErrorCodes.get(diagnostic.code as number));
		})) {
			return results;
		}

		const action = new VsCodeFixAllCodeAction(
			tsAction,
			file,
			tsAction.fixAllDescription || vscode.l10n.t("{0} (Fix all in file)", tsAction.description),
			vscode.CodeActionKind.QuickFix);

		action.diagnostics = [diagnostic];
		action.command = {
			command: ApplyFixAllCodeAction.ID,
			arguments: [<ApplyFixAllCodeAction_args>{ action }],
			title: ''
		};
		results.addFixAllAction(tsAction.fixId, action);
		return results;
	}
}

// Some fix all actions can actually fix multiple differnt diagnostics. Make sure we still show the fix all action
// in such cases
const fixAllErrorCodes = new Map<number, number>([
	// Missing async
	[2339, 2339],
	[2345, 2339],
]);

const preferredFixes = new Map<string, { readonly priority: number; readonly thereCanOnlyBeOne?: boolean }>([
	[fixNames.$yjc, { priority: 2 }],
	[fixNames.$Cjc, { priority: 2 }],
	[fixNames.$Djc, { priority: 2 }],
	[fixNames.$zjc, { priority: 2 }],
	[fixNames.$Gjc, { priority: 2 }],
	[fixNames.$Bjc, { priority: 3 }],
	[fixNames.$Ajc, { priority: 3 }],
	[fixNames.$Ijc, { priority: 2 }],
	[fixNames.$Jjc, { priority: 2 }],
	[fixNames.$Fjc, { priority: 2 }],
	[fixNames.$Hjc, { priority: 0 }],
	[fixNames.$vjc, { priority: 2 }],
	[fixNames.$xjc, { priority: 2 }],
	[fixNames.$wjc, { priority: 2 }],
	[fixNames.$Ejc, { priority: 1, thereCanOnlyBeOne: true }],
]);

function isPreferredFix(
	action: VsCodeCodeAction,
	allActions: readonly VsCodeCodeAction[]
): boolean {
	if (action instanceof VsCodeFixAllCodeAction) {
		return false;
	}

	const fixPriority = preferredFixes.get(action.tsAction.fixName);
	if (!fixPriority) {
		return false;
	}

	return allActions.every(otherAction => {
		if (otherAction === action) {
			return true;
		}

		if (otherAction instanceof VsCodeFixAllCodeAction) {
			return true;
		}

		const otherFixPriority = preferredFixes.get(otherAction.tsAction.fixName);
		if (!otherFixPriority || otherFixPriority.priority < fixPriority.priority) {
			return true;
		} else if (otherFixPriority.priority > fixPriority.priority) {
			return false;
		}

		if (fixPriority.thereCanOnlyBeOne && action.tsAction.fixName === otherAction.tsAction.fixName) {
			return false;
		}

		return true;
	});
}

export function $Ujc(
	selector: DocumentSelector,
	client: ITypeScriptServiceClient,
	fileConfigurationManager: $Kic,
	commandManager: $Chc,
	diagnosticsManager: $aic,
	telemetryReporter: TelemetryReporter
) {
	return $Sic([
		$Vic(client, ClientCapability.Semantic),
	], () => {
		return vscode.languages.registerCodeActionsProvider(selector.semantic,
			new TypeScriptQuickFixProvider(client, fileConfigurationManager, commandManager, diagnosticsManager, telemetryReporter),
			TypeScriptQuickFixProvider.metadata);
	});
}
